import { useState } from 'react';
import { CheckCheck, Download, Eye, Loader2, Mail, Plus, Send, Trash2, Wand2, Zap } from 'lucide-react';
import { downloadFile } from '../../api/client';
import { extractErrorMessage } from '../../api/errors';
import {
  MarketingCampaign,
  useGenerateCampaignCodes,
  useMarkCampaignCodesSent,
  useDeleteCampaignBrevoList,
  useMarketingCampaigns,
  usePushCampaignToBrevo,
} from '../../queries/marketingCampaigns';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';
import { useMarketingOffers } from '../../queries/marketingOffers';
import { useMarketingSegments } from '../../queries/marketingSegments';
import { CampaignCodesDialog } from './CampaignCodesDialog';
import { CampaignBuilderDialog } from './CampaignBuilderDialog';
import { CampaignFormDialog } from './CampaignFormDialog';
import { GeneratePromoEmailDialog } from './GeneratePromoEmailDialog';

const STATUS_LABELS: Record<MarketingCampaign['status'], string> = {
  draft: 'Draft',
  codes_generated: 'Codes generated',
  active: 'Active',
  completed: 'Completed',
};

interface CampaignsPageProps {
  onGeneratePromoEmail?: (context: string, brevoListId: number | null) => void;
}

export function CampaignsPage({ onGeneratePromoEmail }: CampaignsPageProps) {
  const { data: campaigns = [], isLoading } = useMarketingCampaigns();
  const { data: offers = [] } = useMarketingOffers();
  const { data: segments = [] } = useMarketingSegments();
  const generate = useGenerateCampaignCodes();
  const push = usePushCampaignToBrevo();
  const markSent = useMarkCampaignCodesSent();
  const deleteList = useDeleteCampaignBrevoList();

  const [creating, setCreating] = useState(false);
  const [building, setBuilding] = useState(false);
  const [viewingCodes, setViewingCodes] = useState<MarketingCampaign | null>(null);
  const [deletingList, setDeletingList] = useState<MarketingCampaign | null>(null);
  const [generatingEmailFor, setGeneratingEmailFor] = useState<MarketingCampaign | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleGenerate = (campaign: MarketingCampaign) => {
    setError(null);
    setMessage(null);
    generate.mutate(campaign.id, {
      onSuccess: (result) => setMessage(`Generated ${result.generated} codes (${result.skipped} already existed). Total: ${result.total}.`),
      onError: (err) => setError(extractErrorMessage(err, 'Failed to generate codes.')),
    });
  };

  const handlePush = (campaign: MarketingCampaign) => {
    setError(null);
    setMessage(null);
    push.mutate(campaign.id, {
      onSuccess: (result) =>
        setMessage(
          `Pushed ${result.pushed} contacts to Brevo list "${result.brevoListName}".` +
            (result.skippedWithoutConsent > 0 ? ` ${result.skippedWithoutConsent} without marketing consent were left off; their codes stay pending.` : ''),
        ),
      onError: (err) => setError(extractErrorMessage(err, 'Failed to push to Brevo.')),
    });
  };

  const handleMarkSent = (campaign: MarketingCampaign) => {
    setError(null);
    setMessage(null);
    markSent.mutate(campaign.id, {
      onSuccess: (result) => setMessage(`Marked ${result.marked_sent} codes as sent.`),
      onError: (err) => setError(extractErrorMessage(err, 'Failed to mark codes sent.')),
    });
  };

  const handleExport = (campaign: MarketingCampaign) => {
    setError(null);
    downloadFile(`/marketing/campaigns/${campaign.id}/export`, `${campaign.name.replace(/[^a-zA-Z0-9-_]/g, '_')}_codes.csv`).catch((err) =>
      setError(extractErrorMessage(err, 'Failed to export codes.')),
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">Per-user discount/grant code campaigns built from an offer + segment.</p>
        <div className="flex gap-2">
          {/* Build does the discount, segment, offer and campaign together, and
              can send. Create still exists for pairing an offer and segment that
              already exist. */}
          <button
            type="button"
            onClick={() => setCreating(true)}
            className="inline-flex items-center gap-1.5 rounded-md border border-input px-3 py-1.5 text-sm font-medium hover:bg-accent"
          >
            <Plus className="h-4 w-4" />
            From existing offer
          </button>
          <button
            type="button"
            onClick={() => setBuilding(true)}
            className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:opacity-90"
          >
            <Wand2 className="h-4 w-4" />
            Build a campaign
          </button>
        </div>
      </div>

      {message && <p className="text-sm text-green-700 dark:text-green-400">{message}</p>}
      {error && <p className="text-sm text-destructive">{error}</p>}

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-border">
          <table className="w-full text-sm">
            <thead className="bg-muted text-left text-xs font-medium uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-3 py-2">Name</th>
                <th className="px-3 py-2">Offer</th>
                <th className="px-3 py-2">Segment</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Sent</th>
                <th className="px-3 py-2">Redeemed</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {campaigns.map((campaign) => (
                <tr key={campaign.id}>
                  <td className="px-3 py-2 font-medium">{campaign.name}</td>
                  <td className="px-3 py-2 text-muted-foreground">{campaign.offer?.name ?? '—'}</td>
                  <td className="px-3 py-2 text-muted-foreground">{campaign.segment?.name ?? '—'}</td>
                  <td className="px-3 py-2">
                    <span className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">{STATUS_LABELS[campaign.status]}</span>
                  </td>
                  <td className="px-3 py-2 text-muted-foreground">{campaign.sent_codes}/{campaign.total_codes}</td>
                  <td className="px-3 py-2 text-muted-foreground">
                    {campaign.redeemed_codes} ({campaign.total_codes > 0 ? Math.round((campaign.redeemed_codes / campaign.total_codes) * 100) : 0}%)
                  </td>
                  <td className="px-3 py-2 text-right">
                    <div className="flex justify-end gap-2">
                      <button onClick={() => setViewingCodes(campaign)} className="text-muted-foreground hover:text-primary" title="View codes">
                        <Eye className="h-4 w-4" />
                      </button>
                      {campaign.status === 'draft' && (
                        <button onClick={() => handleGenerate(campaign)} disabled={generate.isPending} className="text-muted-foreground hover:text-primary" title="Generate codes">
                          <Zap className="h-4 w-4" />
                        </button>
                      )}
                      {(campaign.status === 'codes_generated' || campaign.status === 'active') && (
                        <button onClick={() => handlePush(campaign)} disabled={push.isPending} className="text-muted-foreground hover:text-primary" title="Push to Brevo">
                          <Send className="h-4 w-4" />
                        </button>
                      )}
                      {campaign.status === 'codes_generated' && (
                        <button onClick={() => handleMarkSent(campaign)} disabled={markSent.isPending} className="text-muted-foreground hover:text-primary" title="Mark all as sent">
                          <CheckCheck className="h-4 w-4" />
                        </button>
                      )}
                      <button onClick={() => handleExport(campaign)} className="text-muted-foreground hover:text-primary" title="Export CSV">
                        <Download className="h-4 w-4" />
                      </button>
                      {/* Offered once the codes are out, when the list is spent.
                          Not done automatically on send: Brevo queues the
                          campaign against the list, so removing it in the same
                          breath risks cutting a send still going out. */}
                      {campaign.brevo_list_id && campaign.sent_codes > 0 && (
                        <button
                          onClick={() => setDeletingList(campaign)}
                          className="text-muted-foreground hover:text-destructive"
                          title="Delete the Brevo list for this campaign"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                      {onGeneratePromoEmail && campaign.brevo_list_id && (
                        <button onClick={() => setGeneratingEmailFor(campaign)} className="text-muted-foreground hover:text-primary" title="Generate promo email">
                          <Mail className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {campaigns.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-3 py-8 text-center text-muted-foreground">
                    No campaigns yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmDialog
        open={deletingList !== null}
        onOpenChange={(open) => !open && setDeletingList(null)}
        title="Delete the Brevo list?"
        description={`Removes the Brevo list for "${deletingList?.name ?? ''}". The contacts and their codes are untouched, and pushing again recreates it. Do this only once the emails have gone out.`}
        confirmLabel="Delete list"
        destructive
        busy={deleteList.isPending}
        onConfirm={() => {
          if (deletingList) deleteList.mutate(deletingList.id);
          setDeletingList(null);
        }}
      />

      {creating && <CampaignFormDialog offers={offers} segments={segments} onClose={() => setCreating(false)} />}
      {building && (
        <CampaignBuilderDialog segments={segments} onClose={() => setBuilding(false)} />
      )}
      {viewingCodes && <CampaignCodesDialog campaignId={viewingCodes.id} campaignName={viewingCodes.name} onClose={() => setViewingCodes(null)} />}
      {generatingEmailFor && (
        <GeneratePromoEmailDialog
          campaign={generatingEmailFor}
          onContinue={(context) => {
            onGeneratePromoEmail?.(context, generatingEmailFor.brevo_list_id ?? null);
            setGeneratingEmailFor(null);
          }}
          onClose={() => setGeneratingEmailFor(null)}
        />
      )}
    </div>
  );
}
